import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Bot, User as UserIcon } from 'lucide-react';
import * as tienda from '@/api/tienda.api';
import type { EntradaDeBitacora } from '@/api/tienda.api';
import { DataTable, type Columna } from '@/components/datos/DataTable';
import { ConDetalle } from '@/components/datos/DetailDrawer';
import { EntityLink } from '@/components/datos/EntityLink';
import { useAbrirDetalle, type Entidad } from '@/components/datos/contexto-detalle';
import { ModuleHeader, Kpi } from '@/components/datos/ModuleHeader';
import { VistasGuardadas } from '@/components/datos/VistasGuardadas';
import { useFiltrosEnUrl } from '@/hooks/useFiltrosEnUrl';
import { numero, fecha, haceCuanto } from '@/lib/formato-tienda';
import { DetalleDeVenta, DetalleDeCliente, DetalleDeProducto, DetalleDePaca } from '@/components/datos/detalles';

/**
 * BITÁCORA — quién hizo qué.
 *
 * ═══ EL FILTRO QUE IMPORTA ═══
 *
 * «Solo lo del agente». Es el primero que Maciel va a usar el día que algo no
 * cuadre, y es la única forma honesta de dejar que un bot toque su negocio:
 * poder revisar en un minuto todo lo que hizo sin que nadie lo mirara.
 *
 * ═══ POR QUÉ «DEL AGENTE» ES UN CAMPO Y NO «SIN USUARIO» ═══
 *
 * Deducirlo de un `userId` vacío mezclaría dos cosas distintas: lo que hizo el
 * agente y lo que se hizo desde un proceso sin sesión (una migración, una
 * tarea nocturna). La primera vez que eso pase, el filtro estaría mintiendo
 * justo en la pantalla que existe para no mentir. Por eso va escrito aparte.
 */

const VISTAS = [
  { id: 'todo', etiqueta: 'Todo' },
  { id: 'agente', etiqueta: 'Solo el agente', ayuda: 'Lo que hizo el bot sin que nadie mirara' },
  { id: 'personas', etiqueta: 'Solo personas' },
] as const;

/**
 * Cómo se dice cada acción en cristiano.
 *
 * Las claves son las que ESCRIBEN los servicios, no las que uno esperaría:
 * `venta.armar` (no `venta.crear`) e `inventario.ajuste` salen de
 * `` `inventario.${tipo.toLowerCase()}` ``. Inventarse el nombre aquí deja la
 * columna enseñando el identificador crudo y nadie se entera, porque no falla.
 */
const ACCIONES: Record<string, string> = {
  'venta.armar': 'Armó una venta',
  'venta.confirmar': 'Confirmó una venta',
  'venta.anular': 'Anuló una venta',
  'venta.cobrar': 'Marcó una venta como cobrada',
  'venta.descobrar': 'Quitó el cobro de una venta',
  'venta.devolver': 'Registró una devolución',
  'inventario.entrada': 'Registró una entrada',
  'inventario.salida': 'Registró una salida',
  'inventario.ajuste': 'Ajustó existencia',
  'inventario.merma': 'Registró una merma',
  'propuesta.crear': 'Pidió permiso para algo',
  'propuesta.aprobada': 'Cerró una propuesta',
  'propuesta.descartada': 'Descartó una propuesta',
};

/**
 * De qué se puede abrir la ficha.
 *
 * Las claves son los nombres que guarda el servidor —`sale`, en inglés, porque
 * es el modelo— y no los de la pantalla. Es el tipo de desajuste que no rompe
 * nada: el enlace simplemente no aparece, y la columna enseña un identificador
 * que no le dice nada a nadie.
 *
 * `inventory_movement` NO está a propósito: un movimiento del libro no tiene
 * ficha que abrir, y un enlace que no lleva a ningún sitio es peor que texto.
 */
const TIPO_DE_ENTIDAD: Record<string, Entidad['tipo']> = {
  sale: 'venta',
  venta: 'venta',
  cliente: 'cliente',
  product: 'producto',
  producto: 'producto',
  paca: 'paca',
};

export default function Bitacora() {
  return (
    <ConDetalle render={(e) => <Detalle entidad={e} />}>
      <Contenido />
    </ConDetalle>
  );
}

function Detalle({ entidad }: { entidad: Entidad }) {
  if (entidad.tipo === 'venta') return <DetalleDeVenta id={entidad.id} />;
  if (entidad.tipo === 'cliente') return <DetalleDeCliente id={entidad.id} />;
  if (entidad.tipo === 'producto') return <DetalleDeProducto id={entidad.id} />;
  if (entidad.tipo === 'paca') return <DetalleDePaca id={entidad.id} />;
  return <p className="text-sm text-slate-500">Nada que mostrar.</p>;
}

function Contenido() {
  const abrir = useAbrirDetalle();
  const { filtros, poner, limpiar, activos, hayFiltros } = useFiltrosEnUrl(
    { vista: 'todo', buscar: '' },
    { vista: VISTAS.map((v) => v.id) },
  );
  const [entradaAbierta, setEntradaAbierta] = useState<EntradaDeBitacora | null>(null);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['tienda', 'bitacora'],
    queryFn: () => tienda.bitacora({ limit: 200 }),
  });
  const entradas = useMemo(() => data ?? [], [data]);
  const delAgente = entradas.filter((e) => e.porElAgente);

  const filtradas = useMemo(() => {
    let salida = entradas;
    if (filtros.vista === 'agente') salida = salida.filter((e) => e.porElAgente);
    if (filtros.vista === 'personas') salida = salida.filter((e) => !e.porElAgente);

    const q = filtros.buscar.trim().toLowerCase();
    if (q) {
      salida = salida.filter((e) =>
        `${ACCIONES[e.accion] ?? e.accion} ${e.accion} ${e.entidad} ${e.usuario?.name ?? ''} ${JSON.stringify(
          e.detalle,
        )}`
          .toLowerCase()
          .includes(q),
      );
    }
    return salida;
  }, [entradas, filtros.vista, filtros.buscar]);

  const columnas: Columna<EntradaDeBitacora>[] = [
    {
      id: 'cuando',
      titulo: 'Cuándo',
      fija: true,
      ancho: 'w-36',
      valor: (e) => e.cuando,
      celda: (e) => (
        <span title={fecha(e.cuando, true)} className="text-slate-600 dark:text-slate-300">
          {haceCuanto(e.cuando)}
        </span>
      ),
    },
    {
      id: 'quien',
      titulo: 'Quién',
      ancho: 'w-40',
      valor: (e) => (e.porElAgente ? 'El agente' : (e.usuario?.name ?? 'Sin sesión')),
      celda: (e) =>
        e.porElAgente ? (
          <span className="inline-flex items-center gap-1.5 text-brand-700 dark:text-brand-400">
            <Bot className="h-4 w-4" />
            El agente
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-slate-700 dark:text-slate-200">
            <UserIcon className="h-4 w-4 text-slate-400" />
            {e.usuario?.name ?? 'Sin sesión'}
          </span>
        ),
    },
    {
      id: 'accion',
      titulo: 'Qué hizo',
      valor: (e) => ACCIONES[e.accion] ?? e.accion,
      celda: (e) => (
        <span className="text-slate-900 dark:text-slate-100">
          {ACCIONES[e.accion] ?? e.accion}
        </span>
      ),
    },
    {
      id: 'entidad',
      titulo: 'Sobre qué',
      valor: (e) => `${e.entidad} ${e.entidadId}`,
      celda: (e) => {
        const tipo = TIPO_DE_ENTIDAD[e.entidad];
        if (!tipo) {
          return (
            <span className="text-slate-500">
              {e.entidad} <span className="font-mono text-xs">{e.entidadId.slice(0, 8)}</span>
            </span>
          );
        }
        const nombre =
          typeof e.detalle?.numero === 'string' ? e.detalle.numero : NOMBRE_DE_ENTIDAD[tipo];
        return <EntityLink tipo={tipo} id={e.entidadId} etiqueta={nombre} />;
      },
    },
    {
      id: 'detalle',
      titulo: 'Detalle',
      valor: (e) => JSON.stringify(e.detalle),
      celda: (e) => {
        const texto = resumirDetalle(e.detalle);
        return texto ? (
          <span className="truncate text-slate-600 dark:text-slate-300">{texto}</span>
        ) : (
          <span className="text-slate-400">—</span>
        );
      },
    },
  ];

  return (
    <div className="space-y-5">
      <ModuleHeader
        titulo="Bitácora"
        subtitulo="Quién hizo qué, y cuándo. Nada de aquí se puede borrar ni editar."
        kpis={
          <>
            <Kpi titulo="Registros" valor={numero(entradas.length)} pie="los últimos 200" />
            <Kpi
              titulo="Del agente"
              valor={numero(delAgente.length)}
              pie="sin que nadie mirara"
              onClick={() => poner('vista', 'agente')}
            />
            <Kpi
              titulo="De personas"
              valor={numero(entradas.length - delAgente.length)}
              onClick={() => poner('vista', 'personas')}
            />
            <Kpi
              titulo="Último movimiento"
              valor={entradas[0] ? haceCuanto(entradas[0].cuando) : '—'}
            />
          </>
        }
      />

      <VistasGuardadas
        vistas={VISTAS.map((v) => ({
          ...v,
          cuenta:
            v.id === 'todo'
              ? entradas.length
              : v.id === 'agente'
                ? delAgente.length
                : entradas.length - delAgente.length,
        }))}
        activa={filtros.vista}
        onCambiar={(v) => poner('vista', v)}
      />

      <DataTable
        filas={filtradas}
        columnas={columnas}
        idDe={(e) => e.id}
        cargando={isLoading}
        error={isError ? (error instanceof Error ? error.message : 'Error') : null}
        onReintentar={() => void refetch()}
        buscar={filtros.buscar}
        onBuscar={(v) => poner('buscar', v)}
        placeholderBuscar="Busca por acción, persona o detalle…"
        filtrosActivos={activos.map((a) => ({
          clave: a.clave,
          etiqueta: `${a.clave}: ${a.valor}`,
          onQuitar: () => poner(a.clave as 'vista' | 'buscar', undefined),
        }))}
        onLimpiarFiltros={hayFiltros ? limpiar : undefined}
        memoria="bitacora"
        exportarComo="bitacora"
        onAbrirFila={(e) => {
          const tipo = TIPO_DE_ENTIDAD[e.entidad];
          if (tipo) abrir?.({ tipo, id: e.entidadId, etiqueta: e.entidad });
          else setEntradaAbierta(e);
        }}
        vacio={{
          titulo: 'Todavía no hay nada registrado',
          descripcion:
            'Cada venta, ajuste y devolución va a quedar aquí con su fecha y su responsable.',
        }}
      />

      {entradaAbierta && (
        <div className="rounded-lg border border-surface-border bg-surface p-4">
          <div className="flex items-start justify-between gap-3">
            <h3 className="font-semibold text-slate-900 dark:text-slate-100">
              {ACCIONES[entradaAbierta.accion] ?? entradaAbierta.accion}
            </h3>
            <button
              type="button"
              onClick={() => setEntradaAbierta(null)}
              className="text-sm text-slate-500 hover:text-slate-700"
            >
              Cerrar
            </button>
          </div>
          <p className="mt-1 text-xs text-slate-500">
            {fecha(entradaAbierta.cuando, true)} ·{' '}
            {entradaAbierta.porElAgente ? 'el agente' : (entradaAbierta.usuario?.name ?? 'sin sesión')}
          </p>
          <pre className="mt-3 overflow-auto rounded bg-surface-muted p-3 text-xs text-slate-700 dark:text-slate-300">
            {JSON.stringify(entradaAbierta.detalle, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

/**
 * El detalle en una línea.
 *
 * Se muestran los campos que se entienden solos (motivo, cantidad, total) y se
 * dejan fuera los identificadores: una columna llena de `cmf3k…` no informa de
 * nada y empuja lo legible fuera de la pantalla.
 */
function resumirDetalle(detalle: Record<string, unknown>): string {
  const partes: string[] = [];
  for (const [clave, valor] of Object.entries(detalle ?? {})) {
    if (valor == null) continue;
    if (clave.toLowerCase().endsWith('id')) continue;
    if (typeof valor === 'object') continue;
    // `porElAgente` ya se dice en la columna «Quién», y `numero` en el enlace.
    // Repetirlos aquí empuja fuera de la pantalla lo que sí aporta.
    if (clave === 'porElAgente' || clave === 'numero') continue;
    partes.push(`${ETIQUETA_DE_CAMPO[clave] ?? clave}: ${String(valor)}`);
  }
  return partes.join(' · ');
}

/** Cómo se llama cada cosa cuando no hay un número que enseñar. */
const NOMBRE_DE_ENTIDAD: Record<Entidad['tipo'], string> = {
  venta: 'la venta',
  cliente: 'la clienta',
  producto: 'el producto',
  paca: 'la paca',
  gasto: 'el gasto',
};

/** Los campos del detalle, en castellano. */
const ETIQUETA_DE_CAMPO: Record<string, string> = {
  total: 'total',
  piezas: 'piezas',
  costoTotal: 'costo',
  motivo: 'motivo',
  cantidad: 'cantidad',
  tipo: 'tipo',
  que: 'qué',
  referencia: 'sobre',
  cobrado: 'cobrada',
};
